/* ==========================================================================
   INGEMA. v4 · capa de movimiento e interacción
   GSAP 3 + ScrollTrigger (cdnjs). Cada animación tiene un motivo:
   - Terrenos: las líneas topográficas se dibujan (al cargar, al entrar o con
     el scroll). En "Lo que viene" cambian de forma y color con cada edición.
   - Banner: entrada por máscaras y foto que se asienta.
   - Wordmark: letras que suben al entrar.
   - Visor + índice de ediciones: cortinilla sincronizada con la lista.
   - Prenda: gira entre espalda y frente y se inclina hacia el cursor.
   - Compra: tallas, disponibilidad y precio en vivo; barra fija en móvil.
   Sin GSAP o con reduced-motion todo queda visible y funcional.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var hasGsap = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';
  var EASE = 'expo.out';

  window.__igMotionReady = true;

  if (!hasGsap || reduce) {
    root.classList.remove('ig-motion');
  } else {
    gsap.registerPlugin(ScrollTrigger);
  }

  var motion = function () { return hasGsap && !reduce; };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  /* ---------- Terrenos: dibujar líneas ---------- */
  function drawPaths(svg, duration, delay) {
    if (!motion() || !svg) return;
    var paths = $$('path', svg);
    gsap.killTweensOf(paths);
    gsap.fromTo(paths, { strokeDashoffset: 1 }, {
      strokeDashoffset: 0,
      duration: duration || 2.6,
      delay: delay || 0,
      ease: 'power2.inOut',
      stagger: { each: 0.018, from: 'random' }
    });
  }

  function terrains(ctx) {
    $$('[data-ig-draw]', ctx).forEach(function (wrap) {
      if (!motion()) return;
      var mode = wrap.dataset.igDraw;
      var svg = wrap.querySelector('svg');
      if (!svg || mode === 'manual') return;
      var section = wrap.closest('section') || wrap.parentNode;
      if (mode === 'load') {
        drawPaths(svg, 2.8, 0.2);
      } else if (mode === 'enter') {
        ScrollTrigger.create({
          trigger: section,
          start: 'top 75%',
          once: true,
          onEnter: function () { drawPaths(svg, 3); }
        });
      } else if (mode === 'scrub') {
        // antes se dibujaban atadas al scroll y quedaban a medias; ahora se completan al entrar
        ScrollTrigger.create({
          trigger: section,
          start: 'top 80%',
          once: true,
          onEnter: function () { drawPaths(svg, 3.2); }
        });
      }
    });
  }

  /* ---------- Banner ---------- */
  function banner(ctx) {
    $$('[data-ig-banner]', ctx).forEach(function (el) {
      if (!motion()) return;
      var img = el.querySelector('[data-ig-banner-media] img');
      var lines = $$('[data-ig-line]', el);
      var fades = $$('[data-ig-fade]', el);
      var tl = gsap.timeline({ defaults: { ease: EASE } });
      if (img) tl.fromTo(img, { scale: 1.14 }, { scale: 1, duration: 2.4 }, 0);
      if (lines.length) tl.to(lines, { y: 0, duration: 1.2, stagger: 0.08 }, 0.25);
      if (fades.length) tl.to(fades, { opacity: 1, duration: 1, stagger: 0.1 }, 0.75);

      if (img) {
        gsap.to(img, {
          yPercent: 10,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top top', end: 'bottom top', scrub: true }
        });
      }
      var content = el.querySelector('.ig-banner__content');
      if (content) {
        gsap.to(content, {
          y: -60,
          opacity: 0.2,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top top', end: 'bottom top', scrub: true }
        });
      }
    });
  }

  /* ---------- Wordmark: letras que suben ---------- */
  function marks(ctx) {
    $$('[data-ig-mark]', ctx).forEach(function (el) {
      if (!motion()) return;
      var letters = $$('.ig-mark__l', el);
      if (!letters.length) return;
      if (el.dataset.igMark === 'intro') {
        gsap.fromTo(letters, { yPercent: 115 }, { yPercent: 0, duration: 1.3, ease: EASE, stagger: 0.07, delay: 0.3 });
        return;
      }
      gsap.fromTo(letters, { yPercent: 115 }, {
        yPercent: 0,
        ease: 'none',
        stagger: 0.08,
        scrollTrigger: { trigger: el, start: 'top 92%', end: 'bottom 55%', scrub: 0.6 }
      });
    });
  }

  /* ---------- Reveal por lotes y títulos con máscara ---------- */
  function reveals(ctx) {
    if (!motion()) return;
    var items = $$('[data-ig-reveal]', ctx);
    if (items.length) {
      ScrollTrigger.batch(items, {
        start: 'top 90%',
        once: true,
        onEnter: function (batch) {
          gsap.to(batch, { y: 0, opacity: 1, duration: 1.1, ease: EASE, stagger: 0.08, overwrite: true });
        }
      });
    }
    $$('[data-ig-line]', ctx).forEach(function (line) {
      if (line.closest('[data-ig-banner]')) return;
      gsap.to(line, {
        y: 0,
        duration: 1.2,
        ease: EASE,
        scrollTrigger: { trigger: line.parentNode, start: 'top 92%', once: true }
      });
    });
  }

  /* ---------- Visor + índice de ediciones (no depende de GSAP) ---------- */
  function slides(ctx) {
    $$('[data-ig-slides]', ctx).forEach(function (el) {
      if (el.__igSlides) return;
      var items = $$('.ig-slide', el);
      var ticks = $$('.ig-tick', el);
      var rows = $$('.ig-index__row', el);
      var pauseBtn = el.querySelector('[data-ig-pause]');
      if (items.length < 2) return;

      var speed = (parseFloat(el.dataset.speed) || 4.5) * 1000;
      var index = 0;
      var timer = null;
      var visible = false;
      var userPaused = reduce;
      el.__igSlides = true;
      if (reduce) el.classList.add('is-paused');

      function go(next) {
        if (next === index || !items[next]) return;
        var prev = items[index];
        var cur = items[next];
        prev.classList.remove('is-active');
        prev.classList.add('is-leaving');
        prev.setAttribute('aria-hidden', 'true');
        cur.classList.remove('is-leaving');
        cur.classList.add('is-active');
        cur.removeAttribute('aria-hidden');
        setTimeout(function () { prev.classList.remove('is-leaving'); }, 1150);

        ticks.forEach(function (t, i) {
          t.classList.toggle('is-done', i < next);
          t.classList.remove('is-active');
        });
        if (ticks[next]) ticks[next].classList.add('is-active');
        rows.forEach(function (r, i) {
          r.classList.toggle('is-active', i === next);
          if (i === next) r.setAttribute('aria-current', 'true'); else r.removeAttribute('aria-current');
        });
        index = next;
      }

      function restartTick() {
        var t = ticks[index];
        if (!t) return;
        t.classList.remove('is-active');
        void t.offsetWidth;
        t.classList.add('is-active');
      }

      function schedule() {
        clearTimeout(timer);
        if (userPaused || !visible || document.hidden) return;
        restartTick();
        timer = setTimeout(function () {
          go((index + 1) % items.length);
          schedule();
        }, speed);
      }

      ticks.concat(rows).forEach(function (t) {
        t.addEventListener('click', function () {
          go(parseInt(t.dataset.go, 10) || 0);
          schedule();
        });
      });

      if (pauseBtn) {
        pauseBtn.addEventListener('click', function () {
          userPaused = !userPaused;
          el.classList.toggle('is-paused', userPaused);
          pauseBtn.setAttribute('aria-label', userPaused ? 'Reproducir' : 'Pausar');
          schedule();
        });
        if (reduce) pauseBtn.setAttribute('aria-label', 'Reproducir');
      }

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          visible = entries[0].isIntersecting;
          el.classList.toggle('is-paused', userPaused || !visible);
          schedule();
        }, { threshold: 0.3 }).observe(el);
      } else {
        visible = true;
        schedule();
      }
      document.addEventListener('visibilitychange', schedule);
    });
  }

  /* ---------- Prenda en escena ---------- */
  function spotlight(ctx) {
    $$('[data-ig-spot]', ctx).forEach(function (el) {
      var stage = el.querySelector('[data-ig-stage]');
      var tee = el.querySelector('[data-ig-tee]');
      if (!stage || !tee) return;
      var sides = $$('.ig-spot__side', tee);
      var buttons = $$('[data-side]', stage);
      var current = 0;

      function show(i) {
        sides.forEach(function (s, k) { s.classList.toggle('is-hidden', k !== i); });
        buttons.forEach(function (b) { b.setAttribute('aria-pressed', String(parseInt(b.dataset.side, 10) === i)); });
        current = i;
      }
      show(0);
      tee.classList.add('is-ready');

      buttons.forEach(function (b) {
        b.addEventListener('click', function () {
          var i = parseInt(b.dataset.side, 10) || 0;
          if (i === current) return;
          if (!motion()) { show(i); return; }
          gsap.timeline()
            .to(tee, { rotationY: 90, duration: 0.32, ease: 'power2.in' })
            .add(function () { show(i); })
            .set(tee, { rotationY: -90 })
            .to(tee, { rotationY: 0, duration: 0.7, ease: 'expo.out' });
        });
      });

      if (!motion()) return;

      gsap.fromTo(tee, { y: 50, rotation: -2 }, {
        y: -30,
        rotation: 0,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'center top', scrub: 0.8 }
      });

      if (fine) {
        var glow = stage.querySelector('.ig-spot__glow');
        var gx = gsap.quickTo(glow, 'x', { duration: 0.9, ease: 'power3.out' });
        var gy = gsap.quickTo(glow, 'y', { duration: 0.9, ease: 'power3.out' });
        var tiltX = gsap.quickTo(tee, 'rotationX', { duration: 0.8, ease: 'power3.out' });
        var shiftX = gsap.quickTo(tee, 'x', { duration: 0.8, ease: 'power3.out' });
        stage.addEventListener('pointermove', function (e) {
          var r = stage.getBoundingClientRect();
          var px = (e.clientX - r.left) / r.width - 0.5;
          var py = (e.clientY - r.top) / r.height - 0.5;
          tiltX(py * -8);
          shiftX(px * 18);
          gx(px * 60);
          gy(py * 60);
        });
        stage.addEventListener('pointerleave', function () { tiltX(0); shiftX(0); gx(0); gy(0); });
      }
    });
  }

  /* ---------- Compra: tallas, disponibilidad y precio ---------- */
  function formatMoney(cents, fmt) {
    var value = (cents / 100);
    var withDelims = function (n, dec, thousands, decimal) {
      var parts = n.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    var out = (fmt || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, function (m, key) {
      switch (key) {
        case 'amount_no_decimals': return withDelims(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelims(value, 2, "'", '.');
        default: return withDelims(value, 2, ',', '.');
      }
    });
    var tmp = document.createElement('div');
    tmp.innerHTML = out;
    return tmp.textContent;
  }

  function buyForms(ctx) {
    $$('[data-ig-buy]', ctx).forEach(function (form) {
      var json = form.querySelector('[data-ig-variants]');
      if (!json) return;
      var variants;
      try { variants = JSON.parse(json.textContent); } catch (e) { return; }
      var idInput = form.querySelector('[data-ig-variant]');
      var btn = form.querySelector('[data-ig-add]');
      var btnText = form.querySelector('[data-ig-add-text]');
      var scope = form.closest('section') || document;
      var priceEl = scope.querySelector('[data-ig-price]');
      var moneyNode = form.querySelector('[data-ig-money]');
      var moneyFmt;
      try { moneyFmt = moneyNode ? JSON.parse(moneyNode.textContent) : null; } catch (e) { moneyFmt = null; }
      var sets = $$('[data-ig-option]', form);

      function selected() {
        return sets.map(function (fs) {
          var c = fs.querySelector('input:checked');
          return c ? c.value : null;
        });
      }

      function match(sel, needAvailable) {
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          if (needAvailable && !v.available) continue;
          var ok = v.options.every(function (o, k) { return sel[k] === null || o === sel[k]; });
          if (ok) return v;
        }
        return null;
      }

      function update() {
        var sel = selected();
        var v = match(sel, false);
        if (!v) {
          btn.disabled = true;
          btnText.textContent = 'No disponible';
        } else {
          idInput.value = v.id;
          btn.disabled = !v.available;
          btnText.textContent = v.available ? btn.dataset.label : btn.dataset.soldout;
          if (priceEl) priceEl.textContent = formatMoney(v.price, moneyFmt);
        }
        sets.forEach(function (fs, idx) {
          $$('.ig-opt__item', fs).forEach(function (item) {
            var test = sel.slice();
            test[idx] = item.querySelector('input').value;
            item.classList.toggle('is-unavailable', !match(test, true));
          });
        });
      }

      form.addEventListener('change', update);
      update();
    });
  }

  /* ---------- Barra de compra fija (móvil) ---------- */
  function stickyBar(ctx) {
    $$('[data-ig-sticky]', ctx).forEach(function (bar) {
      if (!('IntersectionObserver' in window)) return;
      var section = bar.closest('[data-ig-spot]');
      var info = section && section.querySelector('.ig-spot__info');
      var hero = document.querySelector('[data-ig-banner]');
      var end = document.querySelector('#unete') || document.querySelector('footer');
      var link = bar.querySelector('a');
      var state = { heroOut: !hero, infoIn: false, endIn: false };

      function render() {
        var show = state.heroOut && !state.infoIn && !state.endIn;
        bar.classList.toggle('is-shown', show);
        bar.setAttribute('aria-hidden', String(!show));
        if (link) link.tabIndex = show ? 0 : -1;
      }

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.target === hero) state.heroOut = !en.isIntersecting && en.boundingClientRect.top < 0;
          if (en.target === info) state.infoIn = en.isIntersecting;
          if (en.target === end) state.endIn = en.isIntersecting;
        });
        render();
      });
      [hero, info, end].forEach(function (t) { if (t) io.observe(t); });
    });
  }

  /* ---------- Díptico ---------- */
  function diptych(ctx) {
    if (!motion()) return;
    $$('[data-ig-dip]', ctx).forEach(function (el) {
      $$('.ig-dip__item img', el).forEach(function (img, i) {
        gsap.fromTo(img, { scale: 1.16, yPercent: i % 2 ? -4 : 4 }, {
          scale: 1,
          yPercent: 0,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'center center', scrub: 0.8 }
        });
      });
      var seal = el.querySelector('[data-ig-seal]');
      if (seal) {
        gsap.fromTo(seal, { rotation: -10, y: 80, opacity: 0 }, {
          rotation: 6,
          y: -40,
          opacity: 1,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top 70%', end: 'bottom 30%', scrub: 0.8 }
        });
      }
    });
  }

  /* ---------- Lo que viene: color y terreno por edición ---------- */
  function editions(ctx) {
    $$('[data-ig-eds]', ctx).forEach(function (el) {
      var panels = $$('[data-ig-ed]', el);
      var holder = el.querySelector('.ig-eds__terrain');
      var wraps = holder ? $$('.ig-terrain', holder) : [];
      var base = { bg: el.dataset.bg, fg: el.dataset.fg, line: el.dataset.line, id: null };
      var activeId = 'none';

      function terrainFor(id) {
        for (var i = 0; i < wraps.length; i++) {
          if ((id && wraps[i].dataset.terrainFor === id) || (!id && !wraps[i].dataset.terrainFor)) return wraps[i];
        }
        return null;
      }

      function paint(c) {
        el.style.setProperty('--eds-bg', c.bg);
        el.style.setProperty('--eds-fg', c.fg);
        el.style.setProperty('--eds-line', c.line || c.fg);
        if (activeId === c.id) return;
        activeId = c.id;
        var target = terrainFor(c.id);
        wraps.forEach(function (w) {
          var svg = w.querySelector('svg');
          if (svg) svg.classList.toggle('is-on', w === target);
        });
        if (target) drawPaths(target.querySelector('svg'), 2.6);
      }

      if ('IntersectionObserver' in window) {
        var active = null;
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) {
              active = en.target;
              paint({ bg: active.dataset.bg, fg: active.dataset.fg, line: active.dataset.line, id: active.dataset.id });
            } else if (en.target === active && en.boundingClientRect.top > 0 && panels.indexOf(en.target) === 0) {
              active = null;
              paint(base);
            }
          });
        }, { rootMargin: '-45% 0px -45% 0px' });
        panels.forEach(function (p) { io.observe(p); });
      }

      if (!motion()) return;

      /* Primer dibujo del terreno inicial al entrar */
      ScrollTrigger.create({
        trigger: el,
        start: 'top 70%',
        once: true,
        onEnter: function () {
          if (activeId === 'none') { activeId = null; drawPaths(terrainFor(null) && terrainFor(null).querySelector('svg'), 2.6); }
        }
      });

      $$('.ig-ed__media img', el).forEach(function (img) {
        gsap.fromTo(img, { scale: 1.18 }, {
          scale: 1,
          ease: 'none',
          scrollTrigger: { trigger: img.parentNode, start: 'top bottom', end: 'bottom top', scrub: 0.8 }
        });
      });
    });
  }

  function init(ctx) {
    terrains(ctx);
    banner(ctx);
    marks(ctx);
    reveals(ctx);
    slides(ctx);
    spotlight(ctx);
    buyForms(ctx);
    stickyBar(ctx);
    diptych(ctx);
    editions(ctx);
    if (motion()) {
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
      }
      window.addEventListener('load', function () { ScrollTrigger.refresh(); });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (motion()) {
      ScrollTrigger.getAll().forEach(function (t) {
        if (t.trigger && e.target.contains(t.trigger)) t.kill();
      });
    }
    init(e.target);
    if (motion()) ScrollTrigger.refresh();
  });
})();
